import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { Logger } from "@designboticteam/core";
import { CursorSnappingService } from "../../../../input/CursorSnappingService";
import { VERTEX_PICK_PX } from "../../../../input/pickTolerance";
import type { Shape } from "../../../entities/_base/Shape";
import { Polyline } from "../../../entities/Polyline";
import { POINT_EPSILON } from "../../../geometry";
import type { IShapeEditHandler } from "../IShapeEditHandler";

@injectable()
class VertexEditHandler implements IShapeEditHandler {
	private readonly logger = new Logger("✂️ VertexEditHandler", { color: "#ffffff", backgroundColor: "#9333ea" });

	private targetPolyline: Polyline | null = null;
	private selectedPointIndex: number = -1;

	// What the cursor was over when the button went down. The drag only starts once the pointer has
	// travelled past DRAG_THRESHOLD_PX, by which point it may have left the vertex it pressed on — so
	// re-picking at drag start would grab less than the hover feedback promised.
	private vertexAtPointerDown: number = -1;

	constructor(@inject(CursorSnappingService) private readonly cursorSnappingService: CursorSnappingService) {
		this.logger.log("constructed");
	}

	public setTarget(target: Shape | null): void {
		this.clearHighlight();
		this.targetPolyline = target instanceof Polyline ? target : null;
		this.selectedPointIndex = -1;
		this.vertexAtPointerDown = -1;
	}

	public isEngaged(): boolean {
		return this.selectedPointIndex !== -1;
	}

	public onPointerMove(): void {
		const vertexIndex = this.findVertexUnderCursor();
		this.setHighlight({ vertexIndex: vertexIndex === -1 ? null : vertexIndex });
	}

	public onPointerDown(): void {
		this.vertexAtPointerDown = this.findVertexUnderCursor();
	}

	public onDragStart(): void {
		// Deliberately not a fresh pick: the target is the one the press landed on.
		this.selectedPointIndex = this.vertexAtPointerDown;

		if (this.targetPolyline === null || this.selectedPointIndex === -1) {
			return;
		}

		this.cursorSnappingService.setSnapExclusion([
			{
				sceneObject: this.targetPolyline.line,
				vertexIndices: [this.selectedPointIndex],
			},
		]);
	}

	public onDragMove(): void {
		if (this.targetPolyline === null || this.selectedPointIndex === -1) {
			return;
		}

		const { worldPosition } = this.cursorSnappingService.pickPosition();

		if (worldPosition === null) {
			return;
		}

		const points = this.targetPolyline.getPoints();

		// setPoints drops a vertex that lands on its neighbour, which would shift the index this drag
		// holds onto — so the vertex stops just short of the neighbour instead.
		if (this.isOnNeighbour({ points, position: worldPosition })) {
			return;
		}

		points[this.selectedPointIndex].copy(worldPosition);
		this.targetPolyline.setPoints({ points });
	}

	private isOnNeighbour({ points, position }: { points: ReadonlyArray<THREE.Vector3>; position: THREE.Vector3 }): boolean {
		// A ring has as many edges as vertices, so its first and last vertex are neighbours too.
		const isLoop = this.targetPolyline?.getEdgeCount() === points.length;
		const neighbourIndices = [this.selectedPointIndex - 1, this.selectedPointIndex + 1]
			.map((index) => (isLoop ? (index + points.length) % points.length : index))
			.filter((index) => index >= 0 && index < points.length && index !== this.selectedPointIndex);

		return neighbourIndices.some((index) => points[index].distanceToSquared(position) < POINT_EPSILON * POINT_EPSILON);
	}

	public onDragEnd(): void {
		this.selectedPointIndex = -1;
		this.vertexAtPointerDown = -1;
		this.clearHighlight();
		this.cursorSnappingService.setSnapExclusion([]);
	}

	/** Index of the vertex the cursor is over, or -1. Measured in screen pixels. */
	private findVertexUnderCursor(): number {
		if (this.targetPolyline === null) {
			return -1;
		}

		const { screenPosition } = this.cursorSnappingService.pickPosition();

		if (screenPosition === null) {
			return -1;
		}

		const points = this.targetPolyline.getPoints();
		let lowestDistanceSq = VERTEX_PICK_PX * VERTEX_PICK_PX;
		let nearestIndex = -1;

		for (let index = 0; index < points.length; index++) {
			const vertexOnScreen = this.cursorSnappingService.getPositionOnViewport(points[index]);

			if (vertexOnScreen === null) {
				continue;
			}

			const dx = screenPosition.x - vertexOnScreen.x;
			const dy = screenPosition.y - vertexOnScreen.y;
			const distanceSq = dx * dx + dy * dy;

			if (distanceSq < lowestDistanceSq) {
				lowestDistanceSq = distanceSq;
				nearestIndex = index;
			}
		}

		return nearestIndex;
	}

	// Only the current Polyline draws vertex feedback; LegacyPolyline has no such state.
	private setHighlight({ vertexIndex }: { vertexIndex: number | null }): void {
		if (this.targetPolyline instanceof Polyline) {
			this.targetPolyline.setHighlightedVertex({ vertexIndex });
		}
	}

	private clearHighlight(): void {
		this.setHighlight({ vertexIndex: null });
	}
}

export { VertexEditHandler };
